import type { IconName } from '@/components/ui/Icons'

export interface MethodStage {
  stage: string
  name: string
  body: string
  note: string
  icon: IconName
}

export const METHOD_STAGES: MethodStage[] = [
  {
    stage: 'stage 01',
    name: 'Land',
    icon: 'land',
    body: 'The raw bytes are stored exactly as received, before any code interprets them. Everything downstream is a derivation, so the original is always available to re-derive from when a parser improves.',
    note: 'Why it matters: a fix to the parser six months from now must be able to reproduce every historical figure. That is only possible if the input was never mutated in place.',
  },
  {
    stage: 'stage 02',
    name: 'Parse',
    icon: 'parse',
    body: 'A full-fidelity read: every sheet, every row, text cells retained alongside numeric ones. Bracketed negatives, currency symbols and grouped digits are handled as notation rather than left to a type guesser.',
    note: "This is deliberately ours rather than the engine's. The engine's ingester caps sheets and rows and keeps numeric cells only — correct for a language-model digest, fatal for a general ledger.",
  },
  {
    stage: 'stage 03',
    name: 'Discover',
    icon: 'discover',
    body: 'Where the table actually begins, which columns are headers, which rows are group subtotals rather than data, and which blocks are separate tables sharing one sheet.',
    note: 'A subtotal counted as a row double-counts the group. This is the most common way a plausible-looking wrong number gets made.',
  },
  {
    stage: 'stage 04',
    name: 'Profile',
    icon: 'profile',
    body: 'What each column is, established per column: an account code, a date, an amount, a vendor name, a free-text narration. Fan-out is per column rather than one array shared by every consumer.',
    note: 'One shared numeric array is enough to summarise a document. It is not enough to state a fact about a specific account in a specific period.',
  },
  {
    stage: 'stage 05',
    name: 'Calendar',
    icon: 'calendar',
    body: 'Your fiscal year, your period ends, your labels. An Indian financial year runs April to March; a quarter labelled Q1 means different months in different companies, and a projection three quarters out is meaningless without knowing which.',
    note: 'Native granularity is carried through — day, week, month or quarter — because a model trained on the wrong granularity produces a confident, wrong band.',
  },
  {
    stage: 'stage 06',
    name: 'Normalize',
    icon: 'normalize',
    body: 'Units, signs and scale factors resolved. "Figures in ₹ lakh", written once in a title cell three rows above the numbers it governs, is a hundred-thousand-fold error if it is missed.',
    note: 'INR is the primary currency and the notation follows it: lakh and crore grouping, and the rupee symbol in panel prefixes.',
  },
  {
    stage: 'stage 07',
    name: 'Reconcile',
    icon: 'reconcile',
    body: 'Does it add up. Where two documents disagree, or a total does not equal its parts, the discrepancy is surfaced with both readings rather than silently averaged.',
    note: 'A reconciliation difference is information about your books. Hiding it to make a dashboard look clean destroys the most useful thing in the upload.',
  },
  {
    stage: 'stage 08',
    name: 'Assemble',
    icon: 'assemble',
    body: 'Measures become panels: one snapshot, one shape, one schema version. Panels without the measures they need are locked, with the document that would unlock them named.',
    note: 'The client refuses a payload whose schema version differs from the one it was built against, rather than rendering undefined into a figure.',
  },
]

export interface BridgedCapability {
  name: string
  detail: string
}

export const BRIDGED_CAPABILITIES: BridgedCapability[] = [
  {
    name: 'Forecast a series',
    detail:
      'Returns the vendored payload verbatim. The gate that decides whether to trust it lives on our side, because it is a product decision.',
  },
  {
    name: 'Classify and ingest a document',
    detail:
      'Right for non-tabular files and for working out what a document is. Not the path for accounting-grade extraction.',
  },
  {
    name: 'Extract numeric panels from text',
    detail: 'A quick numeric read. Facts come from our own per-column fan-out instead.',
  },
  {
    name: 'Check figures against grounding',
    detail: 'Substitution-aware: catches a number drifting, not just a missing citation.',
  },
  {
    name: 'Probe',
    detail:
      'Reports which capabilities are importable rather than raising. A missing capability is a degraded product, not a dead one.',
  },
]
